/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {Injectable} from '@angular/core';

import {AsyncValidatorFn, ValidatorFn} from './directives/validators';
import {AbstractControl, FormArray, FormControl, FormGroup} from './model';
/**
 * \@whatItDoes Creates an {\@link AbstractControl} from a user-specified configuration.
 * 
 * It is essentially syntactic sugar that shortens the `new FormGroup()`,
 * `new FormControl()`, and `new FormArray()` boilerplate that can build up in larger
 * forms.
 * 
 * \@howToUse 
 * 
 * To use, inject `FormBuilder` into your component class. You can then call its methods
 * directly.
 * 
 * {\@example forms/ts/formBuilder/form_builder_example.ts region='Component'}
 * 
 *  * **npm package**: `\@angular/forms`
 * 
 *  * **NgModule**: {\@link ReactiveFormsModule}
 * 
 * \@stable
 */
export class FormBuilder {
/**
 * Construct a new {\@link FormGroup} with the given map of configuration.
 * Valid keys for the `extra` parameter map are `validator` and `asyncValidator`.
 * 
 * See the {\@link FormGroup} constructor for more details.
 * @param {?} controlsConfig
 * @param {?=} extra
 * @return {?}
 */
group(controlsConfig: {[key: string]: any}, extra: {[key: string]: any}|null = null): FormGroup {
    const /** @type {?} */ controls = this._reduceControls(controlsConfig);
    const /** @type {?} */ validator: ValidatorFn = extra != null ? extra['validator'] : null;
    const /** @type {?} */ asyncValidator: AsyncValidatorFn = extra != null ? extra['asyncValidator'] : null;
    return new FormGroup(controls, validator, asyncValidator);
  }
/**
 * Construct a new {\@link FormControl} with the given `formState`,`validator`, and
 * `asyncValidator`.
 * 
 * `formState` can either be a standalone value for the form control or an object
 * that contains both a value and a disabled status.
 * 
 * @param {?} formState
 * @param {?=} validator
 * @param {?=} asyncValidator
 * @return {?}
 */
control(
      formState: Object, validator?: ValidatorFn|ValidatorFn[]|null,
      asyncValidator?: AsyncValidatorFn|AsyncValidatorFn[]|null): FormControl {
    return new FormControl(formState, validator, asyncValidator);
  }
/**
 * Construct a {\@link FormArray} from the given `controlsConfig` array of
 * configuration, with the given optional `validator` and `asyncValidator`.
 * @param {?} controlsConfig
 * @param {?=} validator
 * @param {?=} asyncValidator
 * @return {?}
 */
array(
      controlsConfig: any[], validator?: ValidatorFn|null,
      asyncValidator?: AsyncValidatorFn|null): FormArray {
    const /** @type {?} */ controls = controlsConfig.map(c => this._createControl(c));
    return new FormArray(controls, validator, asyncValidator);
  }
/**
 * \@internal
 * @param {?} controlsConfig
 * @return {?}
 */
_reduceControls(controlsConfig: {[k: string]: any}): {[key: string]: AbstractControl} {
    const /** @type {?} */ controls: {[key: string]: AbstractControl} = {};
    Object.keys(controlsConfig).forEach(controlName => {
      controls[controlName] = this._createControl(controlsConfig[controlName]);
    });
    return controls;
  }
/**
 * \@internal
 * @param {?} controlConfig
 * @return {?}
 */
_createControl(controlConfig: any): AbstractControl {
    if (controlConfig instanceof FormControl || controlConfig instanceof FormGroup ||
        controlConfig instanceof FormArray) {
      return controlConfig;

    } else if (Array.isArray(controlConfig)) {
      const /** @type {?} */ value = controlConfig[0];
      const /** @type {?} */ validator: ValidatorFn = controlConfig.length > 1 ? controlConfig[1] : null;
      const /** @type {?} */ asyncValidator: AsyncValidatorFn = controlConfig.length > 2 ? controlConfig[2] : null;
      return this.control(value, validator, asyncValidator);

    } else {
      return this.control(controlConfig);
    }
  }
static decorators: DecoratorInvocation[] = [
{ type: Injectable },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
];
}

function FormBuilder_tsickle_Closure_declarations() {
/** @type {?} */
FormBuilder.decorators;
/**
 * @nocollapse
 * @type {?}
 */
FormBuilder.ctorParameters;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
